import { analyticsOnRegistration } from '@fd/common/auth/analytics';
import {
  GoogleProfile,
  loadUserLocalStorage,
  UserType,
  UserZod,
} from '@fd/common/frontend/loadUserFromLocalStorage';
import { normalize } from '@fd/common/normalize';
import { supabase } from '@fd/supabase/index';
import { honoPrivateClient, honoPublicClient } from '../api/honoClient';
import { logout } from './logout';
import posthog from 'posthog-js';
export type AuthResult =
  | {
      ok: 1;
      username: string;
    }
  | {
      ok: 0;
      message: string;
    };

export const signUp = async (email: string, password: string) => {
  const response = await honoPublicClient.auth.signUp.$post({
    json: {
      email,
      password,
      analytics: {
        utm_source: localStorage.getItem('utm_source') || undefined,
        utm_campaign: localStorage.getItem('utm_campaign') || undefined,
        utm_medium: localStorage.getItem('utm_medium') || undefined,
        gclid: localStorage.getItem('gclid') || undefined,
        posthog_sid: posthog.get_session_id(),
      },
    },
  });

  if (response.status === 200) {
    const json = await response.json();
    await supabase.auth.setSession(json);
    await analyticsOnRegistration(email);
    return {
      ok: 1,
      message: 'Signed up successfully',
    };
  }

  const json = await response.json();
  switch (json.code) {
    case 'email_exists':
      return {
        ok: 0,
        message: 'Email already used',
      };
    case 'invalid_email':
      return {
        ok: 0,
        message: 'Invalid email',
      };
    case 'bad_email':
      return {
        ok: 0,
        message: "This email doesn't look real. Please use another one",
      };
    case 'invalid_password':
      return {
        ok: 0,
        message:
          'Password should be at least 8 characters long and include letters and numbers',
      };
    default:
      return {
        ok: 0,
        message: 'Something went wrong',
      };
  }
};

export const signIn = async (email: string, password: string) => {
  const response = await honoPublicClient.auth.signIn.$post({
    json: {
      email,
      password,
    },
  });
  if (response.status === 200) {
    await supabase.auth.setSession(await response.json());
    return {
      ok: 1,
      message: 'Signed in successfully',
    };
  }

  const json = await response.json();
  switch (json.code) {
    case 'invalid_credentials':
      return {
        ok: 0,
        message: 'Invalid email/password',
      };
    case 'email_not_confirmed':
      return {
        ok: 0,
        message: 'Email not confirmed',
      };
    default:
      return {
        ok: 0,
        message: 'Something went wrong',
      };
  }
};

export const signinWithGoogle = async (id_token: string) => {
  const response = await honoPublicClient.auth.signInWithIdToken.$post({
    json: {
      idToken: id_token,
      analytics: {
        utm_source: localStorage.getItem('utm_source') || undefined,
        utm_campaign: localStorage.getItem('utm_campaign') || undefined,
        utm_medium: localStorage.getItem('utm_medium') || undefined,
        posthog_sid: posthog.get_session_id(),
      },
    },
  });
  if (response.ok) {
    const json = await response.json();
    const authData = await supabase.auth.setSession(json);
    if (json.registered && authData.data.user?.email)
      await analyticsOnRegistration(authData.data.user.email);
  }
};

export const requestResetPassword = async (email: string) => {
  const response = await honoPublicClient.auth.requestResetPassword.$post({
    json: {
      email,
    },
  });
  if (response.ok) {
    return { ok: 1, message: 'Email with password reset link sent' };
  }
  return { ok: 0, message: 'Something went wrong' };
};

export const resetPassword = async (password: string) => {
  const response = await honoPrivateClient.auth.resetPassword.$post({
    json: {
      password,
    },
  });
  if (response.ok) {
    await logout();
    return { ok: 1, message: 'Succesfully changed password' };
  }
  try {
    const json = await response.json();
    switch (json.code) {
      case 'invalid_password':
        return {
          ok: 0,
          message:
            'Password should be at least 8 characters long and include letters and numbers',
        };
      default:
        return {
          ok: 0,
          message: 'Something went wrong',
        };
    }
  } catch {
    return {
      ok: 0,
      message: 'Something went wrong',
    };
  }
};

export function saveUserLocalStorage(auth: UserType | undefined) {
  if (auth) {
    localStorage.userContext = JSON.stringify(normalize(UserZod, auth));
  } else {
    delete localStorage.userContext;
  }
}

export function appendGoogleProfileInfo(googleProfile: GoogleProfile) {
  const user = loadUserLocalStorage();
  if (!user) {
    throw new Error('Failed appendGoogleProfileInfo, no basic profile');
  }
  user.googleProfile = googleProfile;
  saveUserLocalStorage(user);
}

export const getUserFromLocalStorage = (): UserType | undefined => {
  const user = loadUserLocalStorage();
  return user;
};
